/**
 * Event summaries (`GET /event-syntheses`). Distinct from events.service.ts (field events).
 */

import { apiClient } from './api-client';
import type {
  EventSummary,
  UpdateEventSummaryDto,
  ListEventSummariesParams,
} from '@/types/event-summary.types';
import type { PaginatedResponse } from '@/types/data-table.types';
import { toLocalDateString } from '@/utils/format-date-utils';
import { XLSX_MIME } from '@/constants/mime-types.constants';

/** Query string for list/export; omit paging when `paginate` is false. */
function buildEventSummariesSearchParams(
  params?: ListEventSummariesParams,
  options?: { paginate?: boolean },
): URLSearchParams {
  const searchParams = new URLSearchParams();
  const paginate = options?.paginate !== false;

  if (paginate) {
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
  }

  if (params?.eventId) searchParams.append('eventId', params.eventId);
  params?.statuses?.forEach((s) => searchParams.append('statuses', s));
  params?.establishmentIds?.forEach((id) => searchParams.append('establishmentIds', id));

  if (params?.dateFrom) searchParams.append('dateFrom', toLocalDateString(params.dateFrom));
  if (params?.dateTo) searchParams.append('dateTo', toLocalDateString(params.dateTo));
  if (params?.search) searchParams.append('search', params.search);

  return searchParams;
}

/** Trigger a browser download for an XLSX blob. */
function triggerBlobDownload(data: Blob, filename: string): void {
  const blob = new Blob([data], { type: XLSX_MIME });
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
}

/** `GET /event-syntheses` — paginated event summaries. */
export const getEventSummaries = async (
  params?: ListEventSummariesParams,
): Promise<PaginatedResponse<EventSummary>> => {
  const queryString = buildEventSummariesSearchParams(params).toString();
  const url = queryString ? `/event-syntheses?${queryString}` : '/event-syntheses';
  const response = await apiClient.get<PaginatedResponse<EventSummary>>(url);
  return response.data;
};

/** `GET /event-syntheses/export` — download XLSX. */
export const exportEventSummariesExcel = async (
  params?: ListEventSummariesParams,
): Promise<void> => {
  const queryString = buildEventSummariesSearchParams(params, { paginate: false }).toString();
  const url = queryString ? `/event-syntheses/export?${queryString}` : '/event-syntheses/export';
  const response = await apiClient.get<Blob>(url, { responseType: 'blob' });
  triggerBlobDownload(
    response.data,
    `syntheses-evenements-${new Date().toISOString().slice(0, 10)}.xlsx`,
  );
};

/** `GET /event-syntheses/:id` */
export const getEventSummary = async (id: string): Promise<EventSummary> => {
  const response = await apiClient.get<EventSummary>(`/event-syntheses/${id}`);
  return response.data;
};

/** `GET /event-syntheses/by-event/:eventId` */
export const getEventSummaryByEvent = async (eventId: string): Promise<EventSummary | null> => {
  try {
    const response = await apiClient.get<EventSummary>(`/event-syntheses/by-event/${eventId}`);
    return response.data;
  } catch {
    return null;
  }
};

/** `PATCH /event-syntheses/:id` */
export const updateEventSummary = async (
  id: string,
  dto: UpdateEventSummaryDto,
): Promise<EventSummary> => {
  const response = await apiClient.patch<EventSummary>(`/event-syntheses/${id}`, dto);
  return response.data;
};
